import { Injectable } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { SessionService } from './session.service';
import { AccountService } from './account.service';
import { SourceService } from './source.service';
import { ClientService } from './client.service';
import { SectionService } from './section.service';
import { AccountSelectModalComponent } from '../components/account-select-modal/account-select-modal.component';
import { ProfilesOnboardingModalComponent } from '../components/profiles-onboarding-modal/profiles-onboarding-modal.component';
import { OrganizationSetupOnboardingModalComponent } from '../components/organization-setup-onboarding-modal/organization-setup-onboarding-modal.component';

export interface OnboardingStep {
  type: 'organization' | 'account' | 'profile';
  completed: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {

  private isOnboardingActive = false;

  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    private router: Router,
    private sessionService: SessionService,
    private accountService: AccountService,
    private sourceService: SourceService,
    private clientService: ClientService,
    private sectionService: SectionService
  ) {}

  /**
   * Verifica e inicia o fluxo de onboarding se necessário
   * Retorna true se o onboarding foi iniciado
   */
  async checkAndStartOnboarding(): Promise<boolean> {
    if (this.isOnboardingActive) {
      return false;
    }

    await this.sessionService.loaded;

    // Passo 0: Se é primeiro acesso e já tem org (criada pelo root), pedir para renomear
    const userId = this.sessionService.user?.id;
    const isFirstAccess = userId && !localStorage.getItem('onboarding_org_named_' + userId);
    if (isFirstAccess && this.hasOrganizations()) {
      await this.askToRenameOrganization();
      // Marca como completado para não perguntar novamente
      localStorage.setItem('onboarding_org_named_' + userId, 'true');
    }

    // Passo 1: Verificar organizações
    const hasOrganizations = this.hasOrganizations();
    if (!hasOrganizations) {
      await this.startOrganizationOnboarding();
      return true;
    }

    // Passo 2: Verificar contas na organização atual
    const hasAccounts = await this.hasAccountsInCurrentOrganization();
    if (!hasAccounts) {
      await this.startAccountOnboarding();
      return true;
    }

    // Passo 3: Verificar perfis próprios
    const hasOwnProfiles = await this.hasOwnProfiles();
    if (!hasOwnProfiles) {
      await this.askAboutProfiles();
      return true;
    }

    return false;
  }

  /**
   * Verifica se o usuário tem organizações (memberships)
   */
  private hasOrganizations(): boolean {
    const memberships = this.sessionService.user?.memberships;
    return memberships && memberships.length > 0;
  }

  /**
   * Verifica se a organização atual tem contas
   */
  private async hasAccountsInCurrentOrganization(): Promise<boolean> {
    const currentSectionId = this.sessionService.sectionId;

    if (!currentSectionId) {
      return false;
    }

    try {
      const accounts = await this.accountService.listAll();
      const accountsInOrg = accounts.filter(acc => acc.sectionId === currentSectionId);
      return accountsInOrg.length > 0;
    } catch (error) {
      console.error('Erro ao verificar contas:', error);
      return false;
    }
  }

  /**
   * Verifica se a conta atual tem perfis próprios configurados
   */
  private async hasOwnProfiles(): Promise<boolean> {
    const accountId = this.sessionService.accountId;

    if (!accountId) {
      return false;
    }

    try {
      const sources = await this.sourceService.list(accountId);
      // Verifica se tem algum perfil próprio (não é interestAcc)
      const ownProfiles = sources.filter(s =>
        s.type && !s.type.includes('interestAcc') && !s.type.includes('hashtag')
      );
      return ownProfiles.length > 0;
    } catch (error) {
      console.error('Erro ao verificar perfis:', error);
      return true; // Em caso de erro, não bloqueia o fluxo
    }
  }

  /**
   * Inicia o onboarding de organização
   */
  async startOrganizationOnboarding(): Promise<void> {
    this.isOnboardingActive = true;
    try {
      await this.openOrganizationModal();
    } catch (e) {
      this.isOnboardingActive = false;
      console.error('Erro no onboarding de organização:', e);
    }
  }

  /**
   * Abre o modal para criar organização
   */
  private async openOrganizationModal(): Promise<void> {
    const modal = await this.modalController.create({
      component: AccountSelectModalComponent,
      componentProps: {
        accounts: [],
        isManagingOrganizations: true,
        mode: 'organization',
        isOnboarding: true,
        onboardingMessage: 'Crie sua primeira organização para começar'
      },
      backdropDismiss: false,
      cssClass: 'fullscreen-modal'
    });

    modal.onDidDismiss().then(async (result) => {
      if (result.role === 'logout') return;

      if (result.data?.isOrganizationCreated || result.data?.data?.id) {
        // Organização criada com sucesso
        const newOrg = result.data?.data || result.data;

        // Atualiza os memberships do usuário
        try {
          let memberships = await this.clientService.getMemberships();

          // Fallback: se Client/memberships retornou vazio (backend sem Client entry),
          // constrói membership a partir dos dados da org recém-criada
          if (!memberships || memberships.length === 0) {
            try {
              const orgs = await this.sectionService.listOrgs();
              if (Array.isArray(orgs) && orgs.length > 0) {
                memberships = orgs.map(org => ({
                  sectionId: org.id,
                  sectionName: org.name,
                  name: org.name,
                  userId: this.sessionService.user?.id,
                  status: 'active'
                }));
              }
            } catch (fallbackErr) {
              // Último fallback: cria membership sintético com dados da org
              memberships = [{
                sectionId: newOrg.id,
                sectionName: newOrg.name,
                name: newOrg.name,
                userId: this.sessionService.user?.id,
                status: 'active'
              }];
            }
          }

          if (this.sessionService.user) {
            this.sessionService.user.memberships = memberships;
          }

          // Seleciona a nova organização
          const newMembership = memberships.find(m => m.sectionId === newOrg.id);
          if (newMembership) {
            await this.sessionService.selectMembership(newMembership);
          } else if (memberships.length > 0) {
            await this.sessionService.selectMembership(memberships[0]);
          }
        } catch (e) {
          console.error('Erro ao atualizar memberships:', e);
        }

        // Próximo passo: criar conta
        await this.startAccountOnboarding();
      } else {
        // Usuário fechou sem criar, reabrir o alerta
        await this.startOrganizationOnboarding();
      }
    });

    await modal.present();
  }

  /**
   * Inicia o onboarding de configuração de organização (renomear a org auto-criada)
   * Deve ser chamado após autoCreateOrganization(), antes de startAccountOnboarding()
   */
  async startOrganizationSetupOnboarding(): Promise<void> {
    const modal = await this.modalController.create({
      component: OrganizationSetupOnboardingModalComponent,
      backdropDismiss: false,
      cssClass: 'fullscreen-modal'
    });

    modal.onDidDismiss().then(async (result) => {
      if (result.role === 'logout') return;
      // Independente do resultado, avança para a criação de conta
      await this.startAccountOnboarding();
    });

    await modal.present();
  }

  /**
   * Inicia o onboarding de conta
   */
  async startAccountOnboarding(): Promise<void> {
    await this.openAccountModal();
  }

  /**
   * Abre o modal para criar conta
   */
  private async openAccountModal(): Promise<void> {
    const currentSectionId = this.sessionService.sectionId;

    const modal = await this.modalController.create({
      component: AccountSelectModalComponent,
      componentProps: {
        accounts: [],
        isManagingOrganizations: false,
        mode: 'account',
        sectionId: currentSectionId,
        isOnboarding: true,
        onboardingMessage: 'Crie sua primeira conta para gerenciar suas redes'
      },
      backdropDismiss: false,
      cssClass: 'fullscreen-modal'
    });

    modal.onDidDismiss().then(async (result) => {

      if (result.role === 'logout') return;

      if (result.data?.isAccountCreated || result.data?.data?.id) {
        // Conta criada com sucesso
        const newAccount = result.data?.data || result.data;

        // Atualiza a sessão com a nova conta
        if (newAccount?.id) {
          this.sessionService.account = newAccount;
          this.sessionService.accountId = newAccount.id;
        }

        // Próximo passo: configurar perfis
        await this.askAboutProfiles();
      } else {
        // Usuário fechou sem criar, reabrir o alerta
        await this.startAccountOnboarding();
      }
    });

    await modal.present();
  }

  /**
   * Pergunta se o usuário quer adicionar perfis próprios
   */
  async askAboutProfiles(): Promise<void> {
    const modal = await this.modalController.create({
      component: ProfilesOnboardingModalComponent,
      backdropDismiss: false,
      cssClass: 'fullscreen-modal'
    });

    modal.onDidDismiss().then((result) => {
      this.isOnboardingActive = false;
      
      if (!result.data?.connectNow) {
        // Usuário escolheu "Fazer Depois", já foi redirecionado no modal
      } else {
      }
    });

    await modal.present();
  }

  /**
   * Pede para o usuário renomear a organização no primeiro acesso
   * (quando a org foi criada pelo root)
   */
  private async askToRenameOrganization(): Promise<void> {
    const memberships = this.sessionService.user?.memberships;
    if (!memberships || memberships.length === 0) return;

    const orgId = memberships[0].sectionId;
    if (!orgId) return;

    this.isOnboardingActive = true;

    const modal = await this.modalController.create({
      component: OrganizationSetupOnboardingModalComponent,
      backdropDismiss: false,
      cssClass: 'fullscreen-modal'
    });

    return new Promise<void>((resolve) => {
      modal.onDidDismiss().then((result) => {
        this.isOnboardingActive = false;
        resolve();
      });

      modal.present();
    });
  }

  /**
   * Reseta o estado do onboarding
   */
  resetOnboarding(): void {
    this.isOnboardingActive = false;
  }
}
